/**
 * Where on the desk you are, worked out from the address and nothing else.
 *
 * Four places and no fifth:
 *
 *   - `#/desk` — **the board somebody lands on**, whichever that is. Deliberately not the id of it,
 *     so that making another board the default changes where this address leads rather than leaving
 *     a bookmark pointing at yesterday's desk.
 *   - `#/desk/boards` — the boards themselves: made, named, copied, cleared, deleted.
 *   - `#/desk/boards/<board>` — one particular board, as a link somebody can send. **Under the list
 *     and not beside it**, because that is what the frame's back control reads: it removes the last
 *     step, so a named board has to sit inside the place you would want to come back out to. It used
 *     to be `#/desk/<board>`, and going back from one landed on the desk instead of on the list.
 *   - `#/desk/boards/<board>/<element>` — that board, jumped to that element and holding it
 *     highlighted. This is what a search hit on a sticky note opens, and it is the same movement the
 *     board's own "find on this board" makes.
 *
 * The address is the truth, as everywhere else in MyLib, so the browser's own back button walks out
 * of each of them and the frame's back control removes the last step without knowing what it means.
 */

export const DESK_AREA = 'desk'

/** The step that means "the boards themselves". Never collides with an id. */
export const BOARDS = 'boards'

export type Place =
  /** The board somebody lands on, whichever it is. */
  | { readonly kind: 'default' }
  | { readonly kind: 'board'; readonly id: string; readonly element: string | null }
  | { readonly kind: 'boards' }

export const DEFAULT_BOARD: Place = { kind: 'default' }

export function placeIn(path: readonly string[]): Place {
  const [first, second, third] = path
  if (first === undefined || first === '') {
    return DEFAULT_BOARD
  }
  if (first === BOARDS) {
    return second === undefined
      ? { kind: 'boards' }
      : { kind: 'board', id: second, element: third ?? null }
  }
  // The shape a named board used to have. Still read, so that a link somebody sent or bookmarked
  // lands where they meant rather than on a board that does not exist.
  return { kind: 'board', id: first, element: second ?? null }
}

/** The address of a place. */
export function pathTo(place: Place): string[] {
  switch (place.kind) {
    case 'boards':
      return [BOARDS]
    case 'board':
      return place.element === null ? [BOARDS, place.id] : [BOARDS, place.id, place.element]
    default:
      return []
  }
}

/** The address that leads to one element on one board — what a search hit is turned into. */
export function pathToElement(board: string, element: string): string[] {
  return [BOARDS, board, element]
}

/**
 * Which board an address means, given the boards there are.
 *
 * `null` when the address names a board this space does not have — a link somebody kept after
 * deleting the board it pointed at. The screen says so rather than silently opening another one.
 */
export function boardIn(
  place: Place,
  boards: readonly { id: string; isDefault: boolean }[],
): string | null {
  if (boards.length === 0) {
    return null
  }
  if (place.kind === 'board') {
    return boards.some((one) => one.id === place.id) ? place.id : null
  }
  const marked = boards.find((one) => one.isDefault)
  return (marked ?? boards[0])?.id ?? null
}
